import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatTL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Minus, Plus, ShoppingCart, Truck, ShieldCheck, Package } from "lucide-react";
import { toast } from "sonner";
import Seo, { productSchema } from "@/components/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    setNotFound(false); setP(null);
    api.get(`/products/${slug}`).then(r => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div data-testid="product-not-found" className="kz-container py-20 text-center">
      <h1 className="font-display text-3xl font-black mb-2">Ürün bulunamadı</h1>
      <p className="text-ink-500 mb-6">Aradığınız ürün kaldırılmış veya adresi değişmiş olabilir.</p>
      <Link to="/katalog" className="kz-btn-primary">Kataloğa Dön</Link>
    </div>
  );

  if (!p) return <div className="kz-container py-20 text-center text-ink-500">Yükleniyor...</div>;

  const isB2B = user?.role === "b2b_dealer" && user?.b2b_status === "approved";
  const pricing = p.pricing || {};
  const showOriginal = p.currency && p.currency !== "TRY";

  return (
    <div className="kz-container py-10">
      <Seo title={p.name}
           description={`${p.name} — ${p.brand || "SGS"} | SKU: ${p.sku} | Fiyat: ${formatTL(pricing.gross_price || 0)} (KDV dahil) | Orijinal ürün, hızlı kargo.`}
           image={p.image_url}
           path={`/urun/${p.slug}`}
           schema={productSchema(p)}/>

      <nav className="text-xs text-ink-500 mb-6 flex items-center gap-1.5">
        <Link to="/" className="hover:text-ink-900">Ana Sayfa</Link>
        <span>/</span>
        <Link to="/katalog" className="hover:text-ink-900">Katalog</Link>
        <span>/</span>
        <span className="text-ink-900">{p.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        <div className="bg-ink-100 aspect-square overflow-hidden border border-ink-200">
          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover"/>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-ink-500 mb-2">{p.brand || "SGS"}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black leading-tight mb-3">{p.name}</h1>
          <div className="font-mono text-sm text-ink-500 mb-6">Stok Kodu: <span className="text-ink-900 font-semibold">{p.sku}</span></div>

          <div className="border-y border-ink-200 py-6 mb-6">
            <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">
              Liste Fiyatı (KDV Hariç): <span className="font-semibold text-ink-900">{pricing.list_price_original ?? p.price} {p.currency}</span>
              {showOriginal && <> · TRY karşılığı: {formatTL(pricing.list_price_try)}</>}
            </div>
            {pricing.special_price && (
              <div className="flex items-center gap-2 mb-1">
                <span data-testid="detail-special-badge" className="text-xs bg-b2b text-white px-2 py-0.5 font-bold">SİZE ÖZEL FİYAT</span>
                <span className="text-sm text-ink-500">Net: {formatTL(pricing.net_price)} (KDV hariç)</span>
              </div>
            )}
            {pricing.campaign_percent > 0 && (
              <div className="flex items-center gap-2 mb-1">
                <span data-testid="detail-campaign-badge" className="text-xs bg-brand text-white px-2 py-0.5 font-bold">KAMPANYA -%{pricing.campaign_percent}</span>
                <span className="text-sm text-ink-500">{pricing.campaign_name}</span>
              </div>
            )}
            {isB2B && pricing.discount_percent > 0 && !pricing.special_price && (
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-b2b text-white px-2 py-0.5 font-bold">BAYİ İSKONTOSU -%{pricing.discount_percent}</span>
                <span className="text-sm text-ink-500">İndirimli net: {formatTL(pricing.net_price)} (KDV hariç)</span>
              </div>
            )}
            <div className="font-display text-4xl md:text-5xl font-black text-ink-900">
              {formatTL(pricing.gross_price || 0)}
            </div>
            <div className="text-xs text-ink-500 mt-1">
              KDV dahil ({formatTL(pricing.kdv)} KDV — %20)
            </div>
            {!user && (
              <div className="mt-3 text-xs text-b2b bg-b2b/10 border border-b2b/30 px-3 py-2">
                💼 Bayi olarak giriş yaparak özel iskonto fiyatlardan yararlanın.
              </div>
            )}
          </div>

          <p className="text-ink-700 mb-6 leading-relaxed">{p.description}</p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-ink-200">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-ink-100"><Minus size={16}/></button>
              <span data-testid="qty-value" className="px-6 font-bold font-mono">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="p-3 hover:bg-ink-100"><Plus size={16}/></button>
            </div>
            <button
              data-testid="add-to-cart-detail"
              onClick={() => { add({ ...p, price: pricing.gross_price || p.price }, qty); toast.success(`${qty} adet sepete eklendi`); }}
              className="flex-1 kz-btn-primary"
            >
              <ShoppingCart size={18}/> Sepete Ekle
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="flex flex-col items-center text-center p-3 bg-ink-100">
              <Truck size={20} className="text-brand mb-2"/>
              <span className="font-semibold">Hızlı Kargo</span>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-ink-100">
              <ShieldCheck size={20} className="text-brand mb-2"/>
              <span className="font-semibold">Orijinal Garanti</span>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-ink-100">
              <Package size={20} className="text-brand mb-2"/>
              <span className="font-semibold">Stok: {p.stock}</span>
            </div>
          </div>
        </div>
      </div>

      {p.specs && Object.keys(p.specs).length > 0 && (
        <div className="mt-16">
          <h2 className="font-display text-2xl font-black mb-4 pb-3 border-b-2 border-ink-900 inline-block">TEKNİK ÖZELLİKLER</h2>
          <table className="w-full max-w-2xl">
            <tbody>
              {Object.entries(p.specs).map(([k, v], i) => (
                <tr key={k} className={i % 2 ? "bg-ink-100" : ""}>
                  <td className="py-3 px-4 font-semibold text-sm">{k}</td>
                  <td className="py-3 px-4 text-sm font-mono">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
